// ProjectWhatNextPanel — Project-level strategic What Next suggestions
// STORY-014/BUG-020: Answers "What is the single most important thing I should do?"
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Shield, Users, BookOpen, DollarSign,
  ChevronDown, ChevronUp, ArrowRight, Sparkles,
} from 'lucide-react'
import { getProjectWhatNext, acceptWhatNextSuggestion } from '../../services/api'
import CreateReviewModal from '../reviews/CreateReviewModal'
import type { ProjectStrategicSuggestion, StrategicCategory } from '../../types'

interface ProjectWhatNextPanelProps {
  projectId: string
}

const CATEGORY_META: Record<StrategicCategory, { icon: React.ElementType; color: string; bgColor: string }> = {
  ip_protection: { icon: Shield, color: 'text-red-700', bgColor: 'bg-red-50 border-red-200' },
  end_user_validation: { icon: Users, color: 'text-blue-700', bgColor: 'bg-blue-50 border-blue-200' },
  expert_review: { icon: BookOpen, color: 'text-amber-700', bgColor: 'bg-amber-50 border-amber-200' },
  grant_alerts: { icon: DollarSign, color: 'text-green-700', bgColor: 'bg-green-50 border-green-200' },
}

export default function ProjectWhatNextPanel({ projectId }: ProjectWhatNextPanelProps) {
  const [suggestions, setSuggestions] = useState<ProjectStrategicSuggestion[]>([])
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [workflowStage, setWorkflowStage] = useState<string>('unknown')
  const [error, setError] = useState(false)

  useEffect(() => {
    setLoading(true)
    setError(false)
    getProjectWhatNext(projectId)
      .then((resp) => {
        setSuggestions(resp.suggestions || [])
        setWorkflowStage(resp.workflow_stage || 'unknown')
      })
      .catch(() => {
        setSuggestions([])
        setError(true)
      })
      .finally(() => setLoading(false))
  }, [projectId])

  const handleDismiss = (suggestionId: string) => {
    setDismissed((prev) => new Set(prev).add(suggestionId))
  }

  const activeSuggestions = suggestions.filter((s) => !dismissed.has(s.suggestion_id))
  const dismissedSuggestions = suggestions.filter((s) => dismissed.has(s.suggestion_id))

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-indigo-50 to-sky-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-sm text-indigo-500">
          <Sparkles className="w-4 h-4 animate-pulse" />
          Loading strategic suggestions...
        </div>
      </div>
    )
  }

  if (error || suggestions.length === 0) {
    return (
      <div className="bg-gradient-to-r from-indigo-50 to-sky-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-indigo-500" />
          <h3 className="text-sm font-semibold text-indigo-900">Strategic What Next</h3>
        </div>
        <p className="text-xs text-indigo-600 italic">
          Upload and analyse documents to get strategic project-level recommendations.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-r from-indigo-50 to-sky-50 border border-indigo-200 rounded-lg p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-indigo-500" />
          <h3 className="text-sm font-semibold text-indigo-900">
            Strategic What Next
          </h3>
          {workflowStage !== 'unknown' && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-600 font-medium">
              Stage: {workflowStage}
            </span>
          )}
        </div>
        <span className="text-[10px] text-indigo-400">
          {activeSuggestions.length} active
        </span>
      </div>

      {/* Active suggestions */}
      {activeSuggestions.map((suggestion) => (
        <SuggestionCard
          key={suggestion.suggestion_id}
          suggestion={suggestion}
          projectId={projectId}
          onDismiss={handleDismiss}
        />
      ))}

      {/* Dismissed suggestions */}
      {dismissedSuggestions.length > 0 && (
        <DismissedSection suggestions={dismissedSuggestions} />
      )}
    </div>
  )
}

// BUG-RETEST-I: map category → backend action_type
const CATEGORY_ACTION_TYPE: Partial<Record<StrategicCategory, 'request_review' | 'plan_interviews' | 'search_grants' | 'start_patent_filing'>> = {
  expert_review: 'request_review',
  end_user_validation: 'plan_interviews',
  grant_alerts: 'search_grants',
  ip_protection: 'start_patent_filing',
}

function SuggestionCard({
  suggestion,
  projectId,
  onDismiss,
}: {
  suggestion: ProjectStrategicSuggestion
  projectId: string
  onDismiss: (id: string) => void
}) {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)
  const [accepting, setAccepting] = useState(false)
  const [toast, setToast] = useState<{ ok: boolean; msg: string } | null>(null)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const meta = CATEGORY_META[suggestion.category] || CATEGORY_META.end_user_validation
  const Icon = meta.icon
  const actionType = CATEGORY_ACTION_TYPE[suggestion.category]

  const handleAccept = async () => {
    if (!actionType || accepting) return
    setAccepting(true)
    setToast(null)
    try {
      const result = await acceptWhatNextSuggestion(projectId, suggestion.suggestion_id, actionType)
      if (actionType === 'search_grants') {
        const gs = result.grants_search as { domain?: string; suggested_programs?: string[] } | undefined
        const n = gs?.suggested_programs?.length ?? 0
        setToast({ ok: true, msg: n > 0 ? `Grant search complete — ${n} program(s) found` : 'Grant search complete — see business.gov.au for listings' })
      } else if (actionType === 'start_patent_filing') {
        setToast({ ok: true, msg: result.idempotent ? 'Already added to your CRM' : 'Todo created — opening Patents' })
        navigate('/patents')
      } else if (actionType === 'request_review') {
        setToast({ ok: true, msg: result.idempotent ? 'Already added to your CRM' : 'Todo created — create review below' })
        setShowReviewModal(true)
      } else if (actionType === 'plan_interviews') {
        setToast({ ok: true, msg: (result.idempotent ? 'Already added to your CRM' : 'Todo created') + ' — interview planner coming soon' })
      } else {
        setToast({ ok: true, msg: result.idempotent ? 'Already added to your CRM' : 'Todo created — see CRM' })
      }
    } catch {
      setToast({ ok: false, msg: 'Action failed — please try again' })
    } finally {
      setAccepting(false)
    }
  }

  return (
    <div className={`border rounded-lg p-3 space-y-2 ${meta.bgColor}`}>
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${meta.color}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span className={`text-sm font-medium ${meta.color}`}>
              {suggestion.title}
            </span>
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-gray-500 hover:text-gray-600 p-0.5"
              aria-label={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>
          <p className="text-xs text-gray-700 mt-1">{suggestion.description}</p>
        </div>
      </div>

      {expanded && (
        <div className="pl-6 space-y-2">
          <div className="bg-white/60 rounded p-2">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Why this matters
            </p>
            <p className="text-xs text-gray-700">{suggestion.why_this_matters}</p>
          </div>
        </div>
      )}

      {/* Toast feedback */}
      {toast && (
        <div className={`pl-6 text-xs font-medium ${toast.ok ? 'text-green-700' : 'text-red-600'}`}>
          {toast.msg}
        </div>
      )}

      <div className="flex items-center gap-2 pl-6">
        {/* BUG-RETEST-I STREAM11B: wired onClick — was missing */}
        <button
          onClick={handleAccept}
          disabled={accepting || !actionType}
          className="flex items-center gap-1 px-2.5 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={`Accept: ${suggestion.action_label}`}
        >
          <ArrowRight className="w-3 h-3" />
          {accepting ? 'Working…' : suggestion.action_label}
        </button>
        <button
          onClick={() => onDismiss(suggestion.suggestion_id)}
          className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-white/50 rounded"
        >
          Dismiss
        </button>
      </div>

      {showReviewModal && <CreateReviewModal onClose={() => setShowReviewModal(false)} />}
    </div>
  )
}

function DismissedSection({ suggestions }: { suggestions: ProjectStrategicSuggestion[] }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="text-xs text-indigo-400">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 cursor-pointer hover:text-indigo-600"
      >
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {suggestions.length} dismissed suggestion(s)
      </button>
      {open && (
        <div className="mt-1 space-y-1 pl-4">
          {suggestions.map((s) => (
            <div key={s.suggestion_id} className="text-gray-500 line-through">
              {s.title}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
